import { post } from '@access-cms/api-client';

import logger from '../../../utils/logger';

export type SignatureAuditEventType =
  | 'ATTESTATION_AGREED'
  | 'ATTESTATION_VIEWED'
  | 'SIGNATURE_ADOPTED'
  | 'SIGNATURE_CLEARED'
  | 'SIGNATURE_DRAWN'
  | 'SIGNATURE_MODAL_OPENED'
  | 'SIGNATURE_STYLE_SELECTED'
  | 'SIGNATURE_SUBMITTED'
  | 'SIGNATURE_TYPE_SELECTED'
  | 'SIGNATURE_UPLOADED';

/**
 * UI payload for POST .../signature-events metadata.
 * Keep in sync with SignatureEventMetadataSchema (@access-cms/validation).
 */
export interface SignatureAuditMetadata {
  attestationTextHash?: string;
  browserLanguage?: string;
  canvasDimensions?: { height: number; width: number };
  canvasFingerprint?: string;
  colorDepth?: number;
  deviceType?: 'desktop' | 'mobile' | 'tablet';
  documentHash?: string;
  inputDevice?: 'mouse' | 'stylus' | 'touch';
  screenResolution?: string;
  signatureType?: 'drawn' | 'styled' | 'uploaded';
  styleIdentifier?: string;
  timestamp?: string;
  timezone?: string;
}

function detectDeviceType(): SignatureAuditMetadata['deviceType'] {
  const userAgent = navigator.userAgent;

  if (/tablet|ipad|silk/i.test(userAgent)) {
    return 'tablet';
  }

  if (/mobile|iphone|ipod|android.*mobile/i.test(userAgent)) {
    return 'mobile';
  }

  return 'desktop';
}

export function buildSignatureAuditMetadata(overrides: SignatureAuditMetadata = {}): SignatureAuditMetadata {
  return {
    browserLanguage: navigator.language,
    colorDepth: screen.colorDepth,
    deviceType: detectDeviceType(),
    screenResolution: `${screen.width}x${screen.height}`,
    timestamp: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...overrides,
  };
}

export async function recordSignatureAuditEvent(
  applicationId: string,
  eventType: SignatureAuditEventType,
  metadata?: SignatureAuditMetadata,
): Promise<void> {
  try {
    await post(
      '/api/v1/applications/{applicationId}/signature-events',
      {
        eventType,
        metadata: metadata as Record<string, unknown> | undefined,
      },
      {
        pathParams: { applicationId },
      },
    );
  } catch (error) {
    logger.warn(
      {
        applicationId,
        err: error instanceof Error ? error.message : String(error),
        eventType,
      },
      'Failed to record signature audit event',
    );
  }
}
